import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { Scale } from 'app/components/uikit/lex-horizontal-stepper/lex-horizontal-stepper.component';

import { Constants } from 'app/constants/constants';
import {
  ScoringStrategy,
  WidgetPredictiveOptions,
} from 'app/modules/widget/components/widget-predictive/widget-predictive.component';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { ActionPlan, ActionPlanService } from 'app/providers/storage-api/actionplan.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

@EnableClassTelemetry()
@Component({
  selector: 'app-action-plan-settings-drawer',
  templateUrl: './action-plan-settings-drawer.component.html',
  styleUrls: ['./action-plan-settings-drawer.component.scss'],
  standalone: false,
})
export class ActionPlanSettingsDrawerComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() open: boolean;
  @Input() actionPlan: ActionPlan;
  @Input() groupId: string;
  @Input() dashboard: Dashboard;

  @Output() close: EventEmitter<ActionPlan | null> = new EventEmitter<ActionPlan | null>();

  isReadOnly: boolean;

  form: UntypedFormGroup;
  minDueDate: Date;
  dueDateStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  targetScore: number;
  scale: Scale;

  isSaving = false;

  constructor(
    authService: AuthService,
    formBuilder: UntypedFormBuilder,
    private actionPlanService: ActionPlanService,
    private alertService: AlertService,
    private translocoService: TranslocoService
  ) {
    this.isReadOnly = authService.isReadOnly;

    this.minDueDate = new Date();
    this.minDueDate.setDate(this.minDueDate.getDate() - 1);

    this.form = formBuilder.group({
      notes: [''],
      dueDate: ['', this.dueDateValidator.bind(this)],
    });

    if (this.isReadOnly) {
      this.form.disable();
    }
  }

  ngOnInit(): void {
    AppService.hideBodyOverflow();
    this.form.patchValue({
      notes: this.actionPlan.notes,
      dueDate: new Date(this.actionPlan.due_date),
    });

    this.targetScore = this.actionPlan.metric_target_value;

    this.setScale();
  }

  onCancel(): void {
    this.close.emit(null);
  }

  async onSave(): Promise<void> {
    this.isSaving = true;

    this.form.disable();

    try {
      const actionPlanToUpdate = {
        ...this.actionPlan,
        metric_target_value: this.targetScore,
        due_date: Utils.getDateIsoString(this.form.controls.dueDate.value),
        notes: this.form.controls.notes.value,
      };

      const updatedActionPlan = await this.actionPlanService.updateActionPlan(
        this.groupId,
        actionPlanToUpdate
      );

      this.close.emit(updatedActionPlan);
    } catch (error) {
      this.alertService.showErrorToast(
        this.translocoService.translate('action-plan-settings-drawer-component.failed-to-update'),
        error
      );
    }

    this.form.enable();

    this.isSaving = false;
  }

  private dueDateValidator(control: AbstractControl): ValidationErrors | null {
    if ((control.touched || !control.pristine) && !control.value) {
      this.dueDateStatus = {
        state: EnumErrorStates.ERROR,
        text: this.translocoService.translate(
          'action-plan-settings-drawer-component.due-date-required'
        ),
      };
      return { required: this.dueDateStatus.text };
    }

    this.dueDateStatus = { state: EnumErrorStates.SUCCESS };
    return null;
  }

  onTargetScoreValueChanged(value: number): void {
    this.targetScore = value;
  }

  get hasChanges(): boolean {
    return (
      this.actionPlan.notes !== this.form.controls.notes.value ||
      this.actionPlan.due_date !== Utils.getDateIsoString(this.form.controls.dueDate.value) ||
      this.actionPlan.metric_target_value !== this.targetScore
    );
  }

  ngOnDestroy(): void {
    AppService.showBodyOverflow();
  }

  private setScale(): void {
    const predictiveWidget = this.dashboard.widgets.find(
      (w) => w.component === Constants.PREDICTIVE_WIDGET_COMPONENT
    ) as Widget<WidgetPredictiveOptions>;

    let min: number;
    let max: number;
    const scoringStrategy = this.actionPlan.metadata.scoringStrategy as ScoringStrategy;
    switch (scoringStrategy) {
      case 'BOX':
        min = Constants.BOX_MIN;
        max = Constants.BOX_MAX;
        break;
      case 'NPS':
        min = Constants.NPS_MIN;
        max = Constants.NPS_MAX;
        break;
      default:
      case 'AVERAGE':
        min = 0;
        max = 1000;
        break;
    }

    let stepPercent: number;
    switch (predictiveWidget?.payload.stepValue) {
      case '1%':
        stepPercent = 0.01;
        break;
      case '5%':
        stepPercent = 0.05;
        break;
      default:
      case '10%':
        stepPercent = 0.1;
        break;
    }

    this.scale = {
      min,
      max,
      step: max * stepPercent,
    };
  }
}
